/**
 * The partner table of the example view (D32): one row per door, and the save request built
 * from the posted form.
 *
 * The server keeps a partner by its door and drops every row it is not sent, so the table
 * shows every door and sends the ones with a name. A logo is never sent: it stays with its
 * door and changes only through an upload.
 */

import type { components } from "@countdown/api-client";

type PartnerView = components["schemas"]["ExamplePartnerView"];
type PartnersMode = components["schemas"]["ExamplePartnersMode"];
type SettingsRequest = components["schemas"]["ExampleSettingsRequest"];

export interface PartnerRow {
  readonly position: number;
  readonly name: string;
  readonly shown: boolean;
  readonly logo: NonNullable<PartnerView["logo"]> | null;
}

/** One row per door, 1 to `fieldCount`; a door without a stored partner is an empty row. */
export function partnerRows(
  partners: readonly PartnerView[],
  fieldCount: number,
): PartnerRow[] {
  const stored = new Map(partners.map((partner) => [partner.position, partner]));
  return Array.from({ length: fieldCount }, (_, index) => {
    const position = index + 1;
    const partner = stored.get(position);
    return {
      position,
      name: partner?.name ?? "",
      shown: partner?.shown ?? true,
      logo: partner?.logo ?? null,
    };
  });
}

/** The input names of one row of the table. */
export function partnerInput(part: "name" | "shown", position: number): string {
  return `${part}_${String(position)}`;
}

const MODES: readonly PartnersMode[] = ["list", "real", "none"];

/**
 * The save request from the posted table: names trimmed, rows without a name left out,
 * "Zeigen" from its checkbox. The server checks the doors again and answers in German.
 */
export function settingsRequest(form: FormData, fieldCount: number): SettingsRequest {
  const mode = String(form.get("partners_mode") ?? "");
  const partners: SettingsRequest["partners"] = [];
  for (let position = 1; position <= fieldCount; position += 1) {
    const name = String(form.get(partnerInput("name", position)) ?? "").trim();
    if (name !== "") {
      partners.push({
        position,
        name,
        shown: form.get(partnerInput("shown", position)) === "on",
      });
    }
  }
  return {
    booked_position: Number(form.get("booked_position") ?? 1),
    partners_mode: (MODES as readonly string[]).includes(mode) ? (mode as PartnersMode) : "list",
    partners,
  };
}
